// AHFC's own pages inside a GAPP packet — the only documents we typeset:
//   • Authorization for Release of Medical Information (PHI Release v2)
//   • Comprehensive Assessment, Care Plan & Caregiver Training — Signature Page
//   • Electronic Signature Certificate (the signing record CAERA appends)
//
// Layouts mirror the .docx sources in docs/GAPP Appendices for signature/ and
// the SignWell packet — boxed statements, signature lines with the label
// beneath — under the current AHFC letterhead (letterhead.js standard: logo +
// name + tagline over a navy rule, contact details in the footer). Every string
// comes from i18n.js, so a Spanish packet renders these pages in Spanish. The
// state appendices are NOT rendered here — see gappOverlay.js.

import React from 'react';
import { pdf, Document, Page, View, Text, StyleSheet, Svg, Line } from '@react-pdf/renderer';
import { ensureCursiveFont, Letterhead } from './pdfPrimitives';
import { COMPANY } from './letterhead';
import { t, fill, packetLabel } from './i18n';

const NAVY = '#1A3A5C';
const STEEL = '#2E5A8A';
const INK = '#000000';   // filled values and signatures are black, as on the state forms
const GREY = '#6B7280';
const RULE = '#B9CBE0';
const PANEL = '#F3F5F9';
const PANEL_BLUE = '#EAF0F8';

const st = StyleSheet.create({
  page: { paddingTop: 36, paddingBottom: 56, paddingHorizontal: 36, fontFamily: 'Helvetica', fontSize: 9.5, color: '#111' },
  body: {},
  title: { fontFamily: 'Helvetica-Bold', fontSize: 12, color: NAVY, textAlign: 'center', marginTop: 4, marginBottom: 12 },
  para: { fontSize: 9.5, lineHeight: 1.45 },
  box: { borderWidth: 0.8, borderColor: RULE, backgroundColor: PANEL, padding: 10, marginBottom: 10 },
  boxBlue: { borderWidth: 0.8, borderColor: RULE, backgroundColor: PANEL_BLUE, padding: 10, marginBottom: 12 },
  boxHead: { fontFamily: 'Helvetica-Bold', fontSize: 10, color: NAVY, marginBottom: 5 },
  sectionHead: { fontFamily: 'Helvetica-Bold', fontSize: 8.5, color: STEEL, marginTop: 8, marginBottom: 4 },
  infoRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: PANEL_BLUE, borderTopWidth: 1.2, borderTopColor: NAVY, paddingVertical: 5, paddingHorizontal: 8, marginTop: 4, marginBottom: 12 },
  infoPair: { flexDirection: 'row', alignItems: 'flex-end', flex: 1, paddingRight: 10 },
  infoLabel: { fontFamily: 'Helvetica-Bold', fontSize: 8.5, color: NAVY, marginRight: 5, flexShrink: 0 },
  infoValue: { fontFamily: 'Helvetica', fontSize: 9.5, color: INK, flex: 1 },
  checks: { flexDirection: 'row', flexWrap: 'wrap', marginBottom: 6 },
  check: { flexDirection: 'row', alignItems: 'center', width: '25%', marginBottom: 4 },
  checkBox: { width: 9, height: 9, borderWidth: 0.8, borderColor: '#333', marginRight: 4, alignItems: 'center', justifyContent: 'center' },
  optRow: { flexDirection: 'row', alignItems: 'center', borderWidth: 0.8, borderColor: RULE, marginBottom: 6 },
  optLabel: { width: 130, backgroundColor: PANEL_BLUE, paddingVertical: 5, paddingHorizontal: 8, fontFamily: 'Helvetica-Bold', fontSize: 8.5, color: NAVY },
  optBody: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 5, flexGrow: 1 },
  optBodyStacked: { flexDirection: 'column', alignItems: 'flex-start', paddingHorizontal: 8, paddingVertical: 5, flexGrow: 1 },
  sigRow: { flexDirection: 'row', marginTop: 14 },
  sigCol: { flexGrow: 1, marginRight: 24 },
  sigValue: { height: 20, justifyContent: 'flex-end', borderBottomWidth: 0.8, borderBottomColor: '#333', paddingBottom: 1 },
  sigScript: { fontFamily: 'Signature', fontSize: 14, color: INK },
  sigPlain: { fontFamily: 'Helvetica', fontSize: 10, color: INK },
  sigLabel: { fontFamily: 'Helvetica-Oblique', fontSize: 7.5, color: GREY, marginTop: 2 },
  footerWrap: { position: 'absolute', bottom: 22, left: 36, right: 36 },
  footerNote: { fontFamily: 'Helvetica-Oblique', fontSize: 7, color: GREY, textAlign: 'center', marginBottom: 3 },
  footerRule: { borderTopWidth: 0.6, borderTopColor: '#D1D5DB', paddingTop: 4, flexDirection: 'row', justifyContent: 'space-between' },
  footerText: { fontSize: 7.5, color: GREY },
  footerLbl: { fontFamily: 'Helvetica-Bold', color: NAVY },
  certTable: { borderWidth: 0.6, borderColor: RULE, marginTop: 8 },
  certHead: { flexDirection: 'row', backgroundColor: NAVY },
  certRow: { flexDirection: 'row', borderTopWidth: 0.4, borderTopColor: RULE },
  certCell: { fontSize: 7.5, paddingVertical: 1.5, paddingHorizontal: 4 },
  certHeadCell: { fontFamily: 'Helvetica-Bold', fontSize: 7.5, color: '#fff', paddingVertical: 3, paddingHorizontal: 5 },
  // Spanish reader's translation
});

// Standard AHFC letterhead footer: contact line + page number under a light rule,
// with an optional document-specific note above it (mirrors letterhead.js).
const Footer = ({ note }) => (
  <View style={st.footerWrap} fixed>
    {note ? <Text style={st.footerNote}>{note}</Text> : null}
    <View style={st.footerRule}>
      <Text style={st.footerText}>
        <Text style={st.footerLbl}>Tel: </Text>{COMPANY.tel}   |   <Text style={st.footerLbl}>Fax: </Text>{COMPANY.fax}   |   <Text style={st.footerLbl}>Email: </Text>{COMPANY.email}   |   <Text style={st.footerLbl}>Web: </Text>{COMPANY.web}
      </Text>
      <Text style={st.footerText} render={({ pageNumber }) => `${pageNumber}`} />
    </View>
  </View>
);

// Checkbox drawn as vector strokes (an "X" glyph inside a 9pt box is unreliable
// with the standard PDF fonts).
const CheckItem = ({ checked, label, width }) => (
  <View style={[st.check, width ? { width } : null]}>
    <View style={st.checkBox}>
      {checked ? (
        <Svg width={7} height={7} viewBox="0 0 7 7">
          <Line x1={0.8} y1={0.8} x2={6.2} y2={6.2} stroke={INK} strokeWidth={1.3} />
          <Line x1={6.2} y1={0.8} x2={0.8} y2={6.2} stroke={INK} strokeWidth={1.3} />
        </Svg>
      ) : null}
    </View>
    <Text style={{ fontSize: 8.5 }}>{label}</Text>
  </View>
);

// Header strip of label/value pairs. Each pair is its own flex row so a long
// Spanish label simply takes the space it needs.
const InfoRow = ({ items, style }) => (
  <View style={[st.infoRow, style]}>
    {items.map(([label, value], i) => (
      <View key={i} style={st.infoPair}>
        <Text style={st.infoLabel}>{label}</Text>
        <Text style={st.infoValue}>{value || ' '}</Text>
      </View>
    ))}
  </View>
);

// Signature line: cursive name (or printed text) sitting on a rule, label under it.
const SigLine = ({ label, value, script, width }) => (
  <View style={[width ? { width } : st.sigCol]}>
    <View style={st.sigValue}>
      <Text style={script ? st.sigScript : st.sigPlain}>{value || ' '}</Text>
    </View>
    <Text style={st.sigLabel}>{label}</Text>
  </View>
);

// ── PHI Release (v2) ─────────────────────────────────────────────────────────
export function PhiReleasePage({ data }) {
  const L = t(data.lang);
  const c = data.client || {};
  const f = data.forms || {};
  const s = (k) => (data.signatures || {})[k] || {};
  const released = f.phiReleased || [];
  const valid = f.phiValid || 'Until end of home care services';
  const signer = f.phiSignerType || 'Parent / Legal Guardian';
  // Spanish labels are longer than the English ones; stack the choices rather
  // than letting them collide on one line.
  const stacked = data.lang === 'es';
  return (
    <Page size="LETTER" style={st.page}>
      <Letterhead />
      <View style={st.body}>
        <Text style={st.title}>{L.phi.title}</Text>
        <InfoRow items={[[L.phi.patientName, c.name], [L.phi.dob, c.dob], [L.phi.medicaid, c.medicaid]]} />
        <View style={st.box}>
          <Text style={st.para}>{fill(L.phi.body, { agency: 'A Heart for Care (Hard Shoulder Group LLC)' })}</Text>
          <Text style={[st.para, { fontFamily: 'Helvetica-Oblique', color: GREY, marginTop: 10 }]}>{L.phi.revoke}</Text>
        </View>

        <Text style={st.sectionHead}>{L.phi.releasedHead}</Text>
        <View style={st.checks}>
          {Object.keys(L.phi.released).map((key) => (
            <CheckItem key={key} checked={released.includes(key)} label={L.phi.released[key]}
              width={data.lang === 'es' ? '50%' : '25%'} />
          ))}
        </View>

        <Text style={st.sectionHead}>{L.phi.expirationHead}</Text>
        <View style={st.optRow}>
          <Text style={st.optLabel}>{L.phi.validLabel}</Text>
          <View style={stacked ? st.optBodyStacked : st.optBody}>
            <CheckItem checked={valid !== 'Other'} label={L.phi.validUntilEnd} width={stacked ? '100%' : 250} />
            <CheckItem checked={valid === 'Other'} label={`${L.phi.other} ${valid === 'Other' ? (f.phiValidOther || '') : ''}`} width={stacked ? '100%' : 200} />
          </View>
        </View>
        <View style={st.optRow}>
          <Text style={st.optLabel}>{L.phi.iAmThe}</Text>
          <View style={stacked ? st.optBodyStacked : st.optBody}>
            {Object.keys(L.phi.signerTypes).map((key) => (
              <CheckItem key={key} checked={signer === key} label={L.phi.signerTypes[key]}
                width={stacked ? '100%' : (key === 'Patient (18+)' ? 130 : 165)} />
            ))}
          </View>
        </View>

        <Text style={st.sectionHead}>{L.phi.signatureHead}</Text>
        <View style={st.sigRow}>
          <SigLine label={L.phi.sigRep} value={s('phi_rep').name} script />
          <SigLine label={L.phi.date} value={s('phi_rep').dateShort} width={160} />
        </View>
        <View style={st.sigRow}>
          <SigLine label={L.phi.printedName} value={f.phiPrintedName || s('phi_rep').name || c.guardian} />
          <SigLine label={L.phi.relationship} value={f.phiRelationship || c.relationship} width={160} />
        </View>
        <View style={st.sigRow}>
          <SigLine label={L.phi.sigWitness} value={s('phi_witness').name} script />
          <SigLine label={L.phi.date} value={s('phi_witness').dateShort} width={160} />
        </View>
      </View>
      <Footer note={L.phi.footer} />
    </Page>
  );
}

// ── Assessment / Care Plan / Caregiver Training — signature page ─────────────
export function NacpSignaturePage({ data }) {
  const L = t(data.lang);
  const c = data.client || {};
  const f = data.forms || {};
  const s = (k) => (data.signatures || {})[k] || {};
  const rnPrint = [s('nacp_rn').name, f.nacpRnLicense ? `${L.nacp.licence} ${f.nacpRnLicense}` : ''].filter(Boolean).join('  ·  ');
  return (
    <Page size="LETTER" style={st.page}>
      <Letterhead />
      <View style={st.body}>
        <Text style={st.title}>{L.nacp.title}</Text>
        <InfoRow style={{ marginTop: 0 }} items={[[L.nacp.member, c.name], [L.nacp.dob, c.dob], [L.nacp.visitDate, f.nacpVisitDate]]} />

        <View style={st.box}>
          <Text style={st.boxHead}>{L.nacp.parentHead}</Text>
          <Text style={st.para}>{L.nacp.parentIntro}</Text>
          <Text style={[st.para, { marginTop: 7 }]}>{L.nacp.section1}</Text>
          <Text style={[st.para, { marginTop: 7 }]}>{L.nacp.section2}</Text>
          <Text style={[st.para, { marginTop: 7 }]}>{L.nacp.section3}</Text>
          <Text style={[st.para, { marginTop: 7 }]}>{L.nacp.parentClose}</Text>
        </View>

        <View style={st.boxBlue}>
          <Text style={st.boxHead}>{L.nacp.rnHead}</Text>
          <Text style={st.para}>{L.nacp.rnBody}</Text>
        </View>

        <View style={st.sigRow}>
          <SigLine label={L.nacp.parentName} value={s('nacp_parent').name || c.guardian} width={190} />
          <View style={{ width: 24 }} />
          <SigLine label={L.nacp.date} value={s('nacp_parent').dateShort} width={110} />
          <View style={{ width: 24 }} />
          <SigLine label={L.nacp.signature} value={s('nacp_parent').name} script />
        </View>
        <View style={st.sigRow}>
          <SigLine label={L.nacp.rnName} value={rnPrint} width={190} />
          <View style={{ width: 24 }} />
          <SigLine label={L.nacp.date} value={s('nacp_rn').dateShort} width={110} />
          <View style={{ width: 24 }} />
          <SigLine label={L.nacp.rnSignature} value={s('nacp_rn').name} script />
        </View>
      </View>
      <Footer note={L.nacp.footer} />
    </Page>
  );
}

// ── Electronic Signature Certificate (signing record) ────────────────────────
// Counterpart of SignWell's signing certificate: one row per signing party
// (delivery address, opened, signed, IP).
const PARTY_COLS = ['20%', '15%', '23%', '14%', '14%', '14%'];

export function CertificatePage({ data }) {
  const L = t(data.lang);
  const c = data.client || {};
  const p = data.packet || {};
  const slots = data.slotsMeta || [];
  const parties = data.parties || [];
  const locale = data.lang === 'es' ? 'es-US' : 'en-US';
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' }) : L.cert.none);
  const allSigned = slots.length > 0 && slots.every((s) => s.signed_at);
  const heads = [L.cert.cols.signer, L.cert.cols.role, L.cert.cols.delivered, L.cert.cols.opened, L.cert.cols.signed, L.cert.cols.ip];
  return (
    <Page size="LETTER" style={st.page}>
      <Letterhead />
      <View style={st.body}>
        <Text style={[st.title, { marginTop: 0, marginBottom: 8 }]}>{L.cert.title}</Text>
        <Text style={[st.para, { textAlign: 'center', color: GREY, marginBottom: 6, fontSize: 8.5 }]}>{L.cert.intro}</Text>
        <View style={[st.box, { padding: 8 }]}>
          {[
            [L.cert.document, packetLabel(data.lang, p.type, p.label)],
            [L.cert.member, `${c.name || ''}${c.id ? `  (${c.id})` : ''}`],
            [L.cert.recordId, p.id || ''],
            [L.cert.status, allSigned
              ? fill(L.cert.statusComplete, { n: slots.length })
              : fill(L.cert.statusPartial, { signed: slots.filter((s) => s.signed_at).length, n: slots.length })],
            [L.cert.created, fmt(p.createdAt)],
            [L.cert.completed, p.completedAt ? fmt(p.completedAt) : L.cert.none],
          ].map(([k, v]) => (
            <View key={k} style={{ flexDirection: 'row', marginBottom: 3 }}>
              <Text style={{ width: 96, fontFamily: 'Helvetica-Bold', fontSize: 8.5, color: NAVY }}>{k}</Text>
              <Text style={{ fontSize: 9, flex: 1 }}>{v}</Text>
            </View>
          ))}
        </View>

        <Text style={st.sectionHead}>{L.cert.signersHead}</Text>
        <View style={st.certTable}>
          <View style={st.certHead}>
            {heads.map((h, i) => <Text key={h} style={[st.certHeadCell, { width: PARTY_COLS[i] }]}>{h}</Text>)}
          </View>
          {parties.map((pt) => (
            <View key={pt.role} style={st.certRow}>
              <Text style={[st.certCell, { width: PARTY_COLS[0] }]}>
                {pt.name || L.cert.unsigned}{'\n'}
                <Text style={{ color: GREY }}>{fill(L.cert.sections, { signed: pt.signedSections, total: pt.sections })}</Text>
              </Text>
              <Text style={[st.certCell, { width: PARTY_COLS[1] }]}>{L.party[pt.role] || pt.role}</Text>
              <Text style={[st.certCell, { width: PARTY_COLS[2] }]}>
                {pt.method === 'caera' ? L.cert.inApp
                  : pt.email ? `${fill(L.cert.emailedTo, { email: pt.email })}${pt.sentAt ? `\n${fill(L.cert.sentOn, { date: fmt(pt.sentAt) })}` : ''}`
                  : pt.method === 'link' ? L.cert.emailedLink
                  : (pt.sentAt ? fill(L.cert.linkSent, { date: fmt(pt.sentAt) }) : L.cert.none)}
              </Text>
              <Text style={[st.certCell, { width: PARTY_COLS[3] }]}>{pt.method === 'caera' ? L.cert.none : fmt(pt.viewedAt)}</Text>
              <Text style={[st.certCell, { width: PARTY_COLS[4] }]}>{fmt(pt.signedAt)}</Text>
              <Text style={[st.certCell, { width: PARTY_COLS[5] }]}>{pt.ip || (pt.method === 'caera' ? L.cert.staffSession : L.cert.none)}</Text>
            </View>
          ))}
        </View>
      </View>
      <Footer note={L.cert.footer} />
    </Page>
  );
}

const PAGES = { phi_release: PhiReleasePage, nacp_signature: NacpSignaturePage, certificate: CertificatePage };

// Render one internal page to a PDF Blob (merged into the packet by gappOverlay).
// kind is a form key or 'certificate'.
export async function renderInternalPage(kind, data) {
  ensureCursiveFont();
  const Comp = PAGES[kind];
  if (!Comp) throw new Error(`Unknown internal page: ${kind}`);
  return pdf(<Document><Comp data={data} /></Document>).toBlob();
}
